// src/pages/StarRec/job-holders/Manage.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { authAPI } from "../../../api/apiConfig";
import {
  getEmployees,
  getEmployeeById,
  blacklistEmployee,
  unblacklistEmployee,
} from "../../../api/jobHolder/jobHolderApi";

/* -------------------- MESSAGE BAR -------------------- */
function MessageBar({ message, type = "info", onClose }) {
  if (!message) return null;

  const alertClass =
    type === "error" ? "alert-danger" : type === "success" ? "alert-success" : "alert-info";

  return (
    <div className={`alert ${alertClass} d-flex justify-content-between align-items-center`}>
      <div>{message}</div>
      <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
        Close
      </button>
    </div>
  );
}

/* -------------------- MODAL -------------------- */
function Modal({ open, title, titleClassName = "", children, onClose }) {
  if (!open) return null;

  return (
    <div
      onMouseDown={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        zIndex: 9999,
      }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        className="bg-white border rounded p-3"
        style={{ width: "min(900px, 100%)", maxHeight: "90vh", overflow: "auto" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className={`m-0 ${titleClassName}`}>{title}</h5>
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* -------------------- VIEW MODAL -------------------- */
function ViewJobHolderModal({
  open,
  jobHolder,
  onClose,
  onBlacklist,
  onUnblacklist,
  processing = false,
}) {
  if (!open || !jobHolder) return null;

  const isBlacklisted = !!(jobHolder.isBlacklisted || jobHolder.blacklisted);

  const Field = ({ label, value, danger = false }) => (
    <div className="mb-3">
      <label className={`form-label fw-semibold ${danger ? "text-danger" : ""}`}>{label}</label>
      <input className="form-control" value={value ?? ""} readOnly />
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isBlacklisted ? "View Blacklisted Job Holder Info" : "View Job Holder Info"}
      titleClassName={isBlacklisted ? "text-danger" : ""}
    >
      <div className="mb-2">
        <small className="text-muted">
          ID: <strong>{jobHolder._id || jobHolder.id}</strong>
        </small>
        {isBlacklisted && (
          <div className="alert alert-danger mt-2 py-2">
            <small>This job holder is currently blacklisted.</small>
          </div>
        )}
      </div>

      <div className="row">
        <div className="col-md-6">
          <Field label="Full Name" value={jobHolder.fullName} danger={isBlacklisted} />
        </div>
        <div className="col-md-6">
          <Field label="Position" value={jobHolder.position} danger={isBlacklisted} />
        </div>

        <div className="col-md-6">
          <Field label="StarRec ID" value={jobHolder.starRecId} danger={isBlacklisted} />
        </div>
        <div className="col-md-6">
          <Field label="TAX ID" value={jobHolder.taxId} danger={isBlacklisted} />
        </div>

        <div className="col-md-6">
          <Field label="Business Name" value={jobHolder.businessName} danger={isBlacklisted} />
        </div>
        <div className="col-md-6">
          <Field label="CTC" value={jobHolder.ctc} danger={isBlacklisted} />
        </div>

        <div className="col-md-6">
          <Field label="Date of Joining" value={jobHolder.dateOfJoining} danger={isBlacklisted} />
        </div>
        <div className="col-md-6">
          <Field label="Date of Exit" value={jobHolder.dateOfExit} danger={isBlacklisted} />
        </div>

        <div className="col-md-6">
          <Field label="Personal Email" value={jobHolder.personalEmail} danger={isBlacklisted} />
        </div>
        <div className="col-md-6">
          <Field label="Mobile Number" value={jobHolder.mobileNumber} danger={isBlacklisted} />
        </div>

        {isBlacklisted && (
          <div className="col-12">
            <Field label="Blacklist Reason" value={jobHolder.blacklistReason} danger />
          </div>
        )}
      </div>

      <div className="d-flex justify-content-end gap-2 mt-3 pt-3 border-top">
        <button className="btn btn-outline-secondary" onClick={onClose} disabled={processing}>
          Back
        </button>

        {!isBlacklisted ? (
          <button
            className="btn btn-outline-danger"
            onClick={() => onBlacklist(jobHolder._id || jobHolder.id)}
            disabled={processing}
          >
            {processing ? "Processing..." : "Blacklist"}
          </button>
        ) : (
          <button
            className="btn btn-outline-success"
            onClick={() => onUnblacklist(jobHolder._id || jobHolder.id)}
            disabled={processing}
          >
            {processing ? "Processing..." : "Remove Blacklist"}
          </button>
        )}
      </div>
    </Modal>
  );
}

export default function Manage() {
  const navigate = useNavigate();

  // auth guard
  useEffect(() => {
    if (!authAPI.isAuthenticated()) {
      navigate("/");
      return;
    }
  }, [navigate]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const [message, setMessage] = useState({ text: "", type: "info" });
  const msgTimer = useRef(null);

  const showMessage = useCallback((text, type = "info") => {
    setMessage({ text, type });
    if (msgTimer.current) clearTimeout(msgTimer.current);
    msgTimer.current = setTimeout(() => setMessage({ text: "", type: "info" }), 3000);
  }, []);

  const [filterCountry, setFilterCountry] = useState("");
  const [filterState, setFilterState] = useState("");
  const [filterCity, setFilterCity] = useState("");

  const [viewOpen, setViewOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedDetails, setSelectedDetails] = useState(null);

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getEmployees({
        country: filterCountry || undefined,
        state: filterState || undefined,
        city: filterCity || undefined,
        blacklisted: false,
      });

      setRows(data);

      if (data.length === 0) showMessage("No job holders found.", "info");
    } catch (e) {
      showMessage(`Failed to load job holders: ${e?.message || e}`, "error");
    } finally {
      setLoading(false);
    }
  }, [filterCountry, filterState, filterCity, showMessage]);

  const fetchBlacklisted = useCallback(async () => {
    try {
      return await getEmployees({
        country: filterCountry || undefined,
        state: filterState || undefined,
        city: filterCity || undefined,
        blacklisted: true,
      });
    } catch (e) {
      showMessage(`Failed to load blacklisted: ${e?.message || e}`, "error");
      return [];
    }
  }, [filterCountry, filterState, filterCity, showMessage]);

  useEffect(() => {
    document.title = "StarRec Manage Job Holders";
    fetchEmployees();
  }, [fetchEmployees]);

  const countries = useMemo(() => {
    const set = new Set(rows.map((r) => r.country).filter(Boolean));
    return Array.from(set).sort();
  }, [rows]);

  const states = useMemo(() => {
    const list = rows
      .filter((r) => (filterCountry ? r.country === filterCountry : true))
      .map((r) => r.state)
      .filter(Boolean);
    return Array.from(new Set(list)).sort();
  }, [rows, filterCountry]);

  const cities = useMemo(() => {
    const list = rows
      .filter((r) => (filterCountry ? r.country === filterCountry : true))
      .filter((r) => (filterState ? r.state === filterState : true))
      .map((r) => r.city)
      .filter(Boolean);
    return Array.from(new Set(list)).sort();
  }, [rows, filterCountry, filterState]);

  const blacklistedCount = useMemo(() => rows.filter((r) => r.isBlacklisted).length, [rows]);

  const openView = useCallback(
    async (row) => {
      const id = row._id || row.id;
      setSelectedId(id);
      setSelectedDetails(null);
      setViewOpen(true);

      try {
        const details = await getEmployeeById(id);
        setSelectedDetails(details);
      } catch (e) {
        // fallback to row
        setSelectedDetails(row);
        showMessage(`Failed to fetch details, showing cached row.`, "info");
      }
    },
    [showMessage]
  );

  const closeView = useCallback(() => {
    setViewOpen(false);
    setSelectedId(null);
    setSelectedDetails(null);
  }, []);

  const handleBlacklist = useCallback(
    async (id) => {
      const reason = window.prompt("Enter blacklist reason (optional):", "") || "";

      if (!window.confirm("Are you sure you want to blacklist this job holder?")) return;

      setProcessing(true);
      try {
        await blacklistEmployee(id, reason);
        showMessage("Blacklisted successfully!", "success");
        closeView();
        await fetchEmployees();
      } catch (e) {
        showMessage(`Blacklist failed: ${e?.message || e}`, "error");
      } finally {
        setProcessing(false);
      }
    },
    [closeView, fetchEmployees, showMessage]
  );

  const handleUnblacklist = useCallback(
    async (id) => {
      if (!window.confirm("Remove from blacklist?")) return;

      setProcessing(true);
      try {
        await unblacklistEmployee(id);
        showMessage("Removed from blacklist!", "success");
        closeView();
        await fetchEmployees();
      } catch (e) {
        showMessage(`Unblacklist failed: ${e?.message || e}`, "error");
      } finally {
        setProcessing(false);
      }
    },
    [closeView, fetchEmployees, showMessage]
  );

  return (
    <div className="container py-4">
      <h1 className="fs-4 fw-bold mb-3">Manage Job Holders</h1>

      <MessageBar
        message={message.text}
        type={message.type}
        onClose={() => setMessage({ text: "", type: "info" })}
      />

      {/* FILTERS */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label">Country</label>
              <select
                className="form-select"
                value={filterCountry}
                onChange={(e) => {
                  setFilterCountry(e.target.value);
                  setFilterState("");
                  setFilterCity("");
                }}
              >
                <option value="">All</option>
                {countries.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">State</label>
              <select
                className="form-select"
                value={filterState}
                onChange={(e) => {
                  setFilterState(e.target.value);
                  setFilterCity("");
                }}
                disabled={!filterCountry}
              >
                <option value="">All</option>
                {states.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">City</label>
              <select
                className="form-select"
                value={filterCity}
                onChange={(e) => setFilterCity(e.target.value)}
                disabled={!filterState}
              >
                <option value="">All</option>
                {cities.map((ct) => (
                  <option key={ct} value={ct}>
                    {ct}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3 d-flex gap-2">
              <button className="btn btn-outline-secondary w-100" onClick={fetchEmployees} disabled={loading}>
                {loading ? "Loading..." : "Refresh"}
              </button>
              <button
                className="btn btn-outline-danger"
                onClick={async () => {
                  const list = await fetchBlacklisted();
                  showMessage(`Blacklisted found: ${list.length}`, "info");
                }}
                title="Fetch blacklisted count from server"
                disabled={loading}
              >
                Blacklisted
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* TABLE */}
      {loading ? (
        <div className="text-center my-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading job holders...</p>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered table-striped align-middle">
            <thead>
              <tr>
                <th>Job Holder Name</th>
                <th>StarRec ID</th>
                <th>TAX ID</th>
                <th>Current Business Name</th>
                <th>Blacklisted</th>
                <th style={{ width: 120 }} className="text-center">
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-muted">
                    No job holders found.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r._id || r.id} className={r.isBlacklisted ? "table-danger" : ""}>
                    <td>{r.fullName}</td>
                    <td>{r.starRecId}</td>
                    <td>{r.taxId}</td>
                    <td>{r.businessName}</td>
                    <td>{r.isBlacklisted ? "Yes" : "No"}</td>
                    <td className="text-center">
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => openView(r)}>
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <div className="mt-2 text-muted">
            <small>Blacklisted in current table: {blacklistedCount}</small>
          </div>
        </div>
      )}

      <ViewJobHolderModal
        open={viewOpen}
        jobHolder={selectedDetails}
        onClose={closeView}
        onBlacklist={handleBlacklist}
        onUnblacklist={handleUnblacklist}
        processing={processing}
      />
    </div>
  );
}
